import ReactEcharts from 'echarts-for-react';
import React from 'react';

/**
 * Higher order component that prevents the given
 * component from update based in the provided settings.
 *
 * @param {*} settings each property of this settings describes a function which name is the exact
 * name of the property in which the change must not trigger the component update. This function is called
 * whenever the corresponding property changes. This function receives the previous state (before the change) and the next
 * state (after the change).
 * @param {*} Component component that must not update in case the properties defined
 * in the settings change.
 */
export default function updatePreventer(settings, Component) {
  class UpdatePreventer extends React.Component {
    // eslint-disable-next-line no-unused-vars
    shouldComponentUpdate(nextProps, nextState) {
      const nextPropsKeys = Object.keys(nextProps);
      const preventUpdateProps = Object.keys(settings);

      this.propagateChanges(preventUpdateProps, this.props, nextProps);

      const shouldUpdate = nextPropsKeys
        /**
         * the component should NOT update if
         * our prevent update props has changed,
         * so we don't verify it.
         */
        .filter((nextPropKey) => !preventUpdateProps.includes(nextPropKey))
        .some((nextPropKey) => {
          const changed = this.hasChanged(
            nextProps[nextPropKey],
            // eslint-disable-next-line react/destructuring-assignment
            this.props[nextPropKey],
          );

          return changed;
        });

      return shouldUpdate;
    }

    setWrappedComponentRef(instance) {
      this.wrappedComponentRef = instance;
    }

    propagateChanges(preventUpdateProps, currentProps, nextProps) {
      preventUpdateProps.forEach((preventUpdateProp) => {
        const prev = currentProps[preventUpdateProp];
        const next = nextProps[preventUpdateProp];

        if (prev !== next) {
          const onChange = settings[preventUpdateProp];
          const bounded = onChange.bind(this.wrappedComponentRef);
          bounded(prev, next);
        }
      });
    }

    isFunction(functionToCheck) {
      return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    hasChanged(first, second) {
      /**
       * If the props is a echart reference
       */
      if ([first, second].some((v) => typeof v === 'object' && v.current instanceof ReactEcharts)) {
        // A verificação da option do echart é necessária para evitar a referência circular
        // que gera erro no método JSON.stringify
        const firstRef = (typeof first === 'object' && first.current && first.current.props.option);
        const secondRef = (typeof second === 'object' && second.current && second.current.props.option);

        return JSON.stringify(firstRef) !== JSON.stringify(secondRef);
      }
      /**
       * if the props are functions
       */
      if (this.isFunction(first)) {
        const toStr = (obj) => ((obj === null || obj === undefined) ? '' : obj.toString());

        const firstStr = toStr(first);
        const secondStr = toStr(second);

        const changed = firstStr !== secondStr;
        return changed;
      }

      /**
       * if the props are custom objects
       */
      if (typeof first === 'object') {
        const firstJson = JSON.stringify(first);
        const secondJson = JSON.stringify(second);

        const changed = firstJson !== secondJson;
        return changed;
      }

      /**
       * if it is primitives.
       */
      const changed = first !== second;
      return changed;
    }

    render() {
      const { forwardRef } = this.props;

      return (
        <Component
          {...this.props}
          ref={(wrappedComponentRef) => {
            if (forwardRef) {
              forwardRef(wrappedComponentRef);
            }
            this.wrappedComponentRef = wrappedComponentRef;
          }}
        />
      );
    }
  }

  return React.forwardRef((props, ref) => <UpdatePreventer {...props} forwardRef={ref} />);
}
